import React, { useState } from "react";
import { ShieldAlert, Ban, Clock, Mail, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/config/brand";

const CATEGORY_TEXT = {
  harassment: "unwanted or repeated messages toward another person",
  safety: "content that isn't allowed on Maziara",
  spam: "spam or scam-like activity",
  contact: "repeatedly trying to share contact details",
  minor: "content or claims involving someone under 18",
  other: "a breach of our community guidelines",
};

export default function EnforcementScreen({ enforcement }) {
  const { logout } = useAuth();
  const banned = enforcement?.state === "banned";
  const [msg, setMsg] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const reason = CATEGORY_TEXT[enforcement?.category] || CATEGORY_TEXT.other;
  const until = enforcement?.until ? new Date(enforcement.until) : null;

  const submitAppeal = async () => {
    setBusy(true);
    try {
      const r = await api.post("/appeals", { message: msg });
      setSent(true);
      toast.success(r.data.status === "already_submitted" ? "You've already sent an appeal — we'll be in touch." : "Appeal submitted. We'll review it and email you.");
    } catch {
      toast.error("We couldn't submit your appeal right now. Please email us instead.");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#0A0908] bg-noise px-5 py-10 text-center" data-testid={banned ? "ban-screen" : "suspension-screen"}>
      <div className="w-full max-w-md rounded-2xl border border-[#5C1D24]/50 bg-[#160d0e] p-8">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#361217]">
          {banned ? <Ban className="h-7 w-7 text-[#E5817A]" /> : <ShieldAlert className="h-7 w-7 text-[#E5C158]" />}
        </div>
        <h1 className="mt-5 font-display text-2xl text-[#F4F1EA]">
          {banned ? "Your account has been closed" : "Your account is temporarily paused"}
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-[#C8C2B5]">
          {banned
            ? `We closed your ${BRAND.name} account because of ${reason}. This decision is permanent, but you can ask us to take another look.`
            : `We've paused your account because of ${reason}. You can't start new chats or matches during this time. Everything else stays safe on your account.`}
        </p>
        {!banned && until && (
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#211B12] px-4 py-2 text-sm text-[#E5C158]" data-testid="suspension-until">
            <Clock className="h-4 w-4" /> Access returns on {until.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
          </div>
        )}

        <div className="mt-6 border-t border-[#2B241B] pt-6 text-left">
          <p className="mb-2 text-sm font-semibold text-[#F4F1EA]">Think this was a mistake?</p>
          {sent ? (
            <div className="flex items-center gap-2 rounded-xl bg-[#12281a] px-4 py-3 text-sm text-[#9FE0B0]" data-testid="appeal-sent"><Check className="h-4 w-4" /> Your appeal has been received.</div>
          ) : (
            <>
              <textarea data-testid="appeal-input" value={msg} onChange={(e) => setMsg(e.target.value)} rows={3}
                placeholder="Tell us what happened (optional)…"
                className="w-full rounded-xl border border-[#3D3428] bg-[#12100E] px-4 py-3 text-sm text-[#F4F1EA] outline-none focus:border-[#D4AF37]/50" />
              <button data-testid="appeal-submit" disabled={busy} onClick={submitAppeal}
                className="mt-3 w-full rounded-full bg-[#D4AF37] py-3 text-sm font-semibold text-[#0A0908] hover:bg-[#E5C158] disabled:opacity-60">
                {busy ? "Sending…" : "Submit an appeal"}
              </button>
            </>
          )}
          <a href={`mailto:${BRAND.supportEmail}?subject=Account appeal`} className="mt-3 flex items-center justify-center gap-2 text-xs text-[#8C8578] hover:text-[#D4AF37]">
            <Mail className="h-3.5 w-3.5" /> Or email {BRAND.supportEmail}
          </a>
        </div>

        <button data-testid="enforcement-signout" onClick={async () => { await logout(); window.location.href = "/"; }}
          className="mt-6 text-sm text-[#8C8578] hover:text-[#C8C2B5]">Sign out</button>
      </div>
    </div>
  );
}
